import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import { makePost } from '../../../api/ajax';
import { Error } from '../../../components/Form';

import './sass/mfa-code-form.scss';

const CODE_LENGTH = 6;

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .trim()
    .matches(/^\d{6}$/, 'Please enter the 6-digit code')
    .required('Verification code is required'),
});

const handleSubmit = (code, setErrorMessage, setSubmitting, onLoginSuccess) => {
  setErrorMessage('');

  makePost('session/mfa/code', { code }).subscribe(
    ({ response }) => {
      setSubmitting(false);
      onLoginSuccess(response);
    },
    ({ response }) => {
      setSubmitting(false);
      setErrorMessage(response?.error || 'Invalid or expired verification code. Please try again.');
    },
  );
};

/**
 * Displayed right after a successful login when the API reports that
 * multi factor authentication is still pending (`mfaPending: true`).
 * The user is asked to enter the one-time code that was sent to their e-mail.
 *
 * The code is entered into six single-digit boxes: typing advances to the next
 * box, backspace steps back, pasting distributes the digits, and the form
 * submits itself as soon as all six digits are present.
 */
const MfaCodeForm = ({ email, onLoginSuccess, onBack }) => {
  const [errorMsg, setErrorMessage] = useState('');
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(''));
  const inputRefs = useRef([]);

  const focusBox = (index) => {
    const input = inputRefs.current[index];
    if (input) {
      input.focus();
      input.select();
    }
  };

  // Writes the digits into state, keeps Formik's `code` in sync and
  // submits once every box is filled.
  const commit = (next, setFieldValue, submitForm, isSubmitting) => {
    setDigits(next);
    const code = next.join('');
    setFieldValue('code', code);

    if (code.length === CODE_LENGTH && !isSubmitting) {
      // Let Formik pick up the new value before validating.
      setTimeout(submitForm, 0);
    }
  };

  const handleChange = (index, rawValue, formikProps) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;
    const typed = rawValue.replace(/\D/g, '');

    // A whole code pasted (or autofilled) into one box.
    if (typed.length > 1) {
      const next = Array(CODE_LENGTH).fill('');
      typed.slice(0, CODE_LENGTH).split('').forEach((digit, i) => {
        next[i] = digit;
      });
      commit(next, setFieldValue, submitForm, isSubmitting);
      focusBox(Math.min(typed.length, CODE_LENGTH) - 1);
      return;
    }

    const next = [...digits];
    next[index] = typed.slice(-1);
    commit(next, setFieldValue, submitForm, isSubmitting);

    if (next[index] && index < CODE_LENGTH - 1) focusBox(index + 1);
  };

  const handleKeyDown = (index, event, formikProps) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;

    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      commit(next, setFieldValue, submitForm, isSubmitting);
      focusBox(index - 1);
    } else if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (event, formikProps) => {
    const { setFieldValue, submitForm, isSubmitting } = formikProps;
    const pasted = (event.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pasted) return;

    event.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted.slice(0, CODE_LENGTH).split('').forEach((digit, i) => {
      next[i] = digit;
    });
    commit(next, setFieldValue, submitForm, isSubmitting);
    focusBox(Math.min(pasted.length, CODE_LENGTH) - 1);
  };

  const handleLogout = () => {
    setErrorMessage('');
    makePost('session/logout', {}).subscribe(
      () => {
        onBack();
      },
      () => {
        onBack();
      },
    );
  };

  return (
    <div className="mfa-code-form">
      <p className="mfa-code-form__lead">
        We&apos;ve sent a 6-digit verification code to
        {' '}
        {email ? <strong>{email}</strong> : 'your e-mail address'}
        <br />
        Enter it below to finish signing in.
      </p>

      <Formik
        initialValues={{ code: '' }}
        validationSchema={validationSchema}
        onSubmit={({ code }, { setSubmitting }) => handleSubmit(code.trim(), setErrorMessage, setSubmitting, onLoginSuccess)}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(formikProps) => {
          const { values, errors, isSubmitting } = formikProps;

          return (
            <Form className="mfa-code-form__form">
              <label className="mfa-code-form__label" htmlFor="mfa-code-0">
                Verification code
              </label>

              <div className="mfa-code-form__boxes" onPaste={(event) => handlePaste(event, formikProps)}>
                {digits.map((digit, index) => (
                  <React.Fragment key={index}>
                    {index === CODE_LENGTH / 2 && <span className="mfa-code-form__divider" />}
                    <input
                      id={`mfa-code-${index}`}
                      ref={(element) => { inputRefs.current[index] = element; }}
                      className={`mfa-code-form__box${errors.code ? ' is-invalid' : ''}`}
                      type="text"
                      inputMode="numeric"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      maxLength={CODE_LENGTH}
                      aria-label={`Digit ${index + 1}`}
                      value={digit}
                      disabled={isSubmitting}
                      onChange={(event) => handleChange(index, event.target.value, formikProps)}
                      onKeyDown={(event) => handleKeyDown(index, event, formikProps)}
                      onFocus={(event) => event.target.select()}
                    />
                  </React.Fragment>
                ))}
              </div>

              {typeof errors.code === 'string' && (
                <div className="mfa-code-form__field-error">{errors.code}</div>
              )}

              <button
                type="submit"
                className="btn btn-primary mfa-code-form__submit"
                disabled={isSubmitting || values.code.length !== CODE_LENGTH}
              >
                Verify code
              </button>
            </Form>
          );
        }}
      </Formik>

      <div>
        <button
          type="button"
          className="btn-link"
          onClick={(event) => {
            event.preventDefault();
            handleLogout();
          }}
        >
          Back to login
        </button>
        {!!errorMsg && <Error>{errorMsg}</Error>}
      </div>
    </div>
  );
};

MfaCodeForm.propTypes = {
  email: PropTypes.string,
  onLoginSuccess: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default MfaCodeForm;
